import styled, { useMediaQuery } from 'academy-common-lib/utils/Styling';
import PublicContentCard from '../../publicPresentation/components/PublicContentCard';
import usageTracker from '../../../utils/usageTracker';
import { getProxyUrl, getPublicLang } from 'academy-common-lib/utils/url';
import UILoadingDots from 'UIComponents/loading/UILoadingDots';
import { NavMarker } from 'react-rhumb';
import ACBadRequestError from 'academy-common-lib/components/error/ACBadRequestError';
import UIErrorMessage from 'UIComponents/error/UIErrorMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import DocumentHead from '../../publicSeo/components/DocumentHead';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import { usePagination } from '../publicCatalogPaginationHooks';
import Schema from '../../publicSeo/components/Schema';
import { getOverviewModalParams } from 'academy-common-lib/utils/url';
import { useGetCatalogResultsFromUrlFilters } from '../publicCatalogResultsHooks';
import { useRef } from 'react';
import { getSignupUrl } from '../../../utils/urls';
import { useIsLoggedIn } from '../../publicLayout/publicQueries';
import { ThemedLinkMixin } from '../../../components/ThemedComponents';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const CardGrid = styled.div.withConfig({
  displayName: "PublicCatalogResults__CardGrid",
  componentId: "q0wedu-0"
})(["display:grid;justify-items:center;gap:32px;grid-template-columns:repeat(auto-fill,minmax(298px,1fr));"]);
const ThemedPaginator = styled(UIPaginator).withConfig({
  displayName: "PublicCatalogResults__ThemedPaginator",
  componentId: "q0wedu-1"
})(["button{", "}"], ThemedLinkMixin);
export const PAGE_SIZE = 12;
const getCardContentType = (contentType, hasAward) => {
  if (hasAward) return 'CERTIFICATION';
  if (contentType === 'TRACK') return 'COURSE';
  if (contentType === 'LESSON') return 'LESSON';
  return null;
};
const getResultHrefs = (result, isLoggedIn) => {
  const modalOverviewParams = getOverviewModalParams({
    id: result.id,
    type: result.contentType === 'LESSON' ? 'LESSON' : 'TRACK',
    source: 'public-catalog',
    language: getPublicLang()
  });
  const internalOverviewHref = `https://app.hubspot.com/academy/l/?${new URLSearchParams(modalOverviewParams)}`;
  const internalSignupHref = getSignupUrl(result.contentType === 'LESSON' ? 'LESSON' : 'COURSE', result.id);
  const externalHref = getProxyUrl(`${result.contentType === 'LESSON' ? 'lessons' : 'courses'}/${result.pagePath}`);
  return {
    href: result.pagePath ? externalHref : internalOverviewHref,
    footerHref: isLoggedIn ? internalOverviewHref : internalSignupHref
  };
};
const ResultsSchema = ({
  resultsData
}) => {
  var _resultsData$results;
  if (!(resultsData !== null && resultsData !== void 0 && (_resultsData$results = resultsData.results) !== null && _resultsData$results !== void 0 && _resultsData$results.length)) return null;
  return /*#__PURE__*/_jsx(DocumentHead, {
    children: /*#__PURE__*/_jsx(Schema, {
      object: {
        '@type': 'ItemList',
        numberOfItems: resultsData.total,
        itemListElement: resultsData.results.map((result, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          url: getResultHrefs(result, false).href,
          name: result.name
        }))
      }
    })
  });
};
export const PublicCatalogResultsDisplay = ({
  data,
  error,
  loading,
  disablePaginator = false,
  disableSchema = false
}) => {
  var _error$status, _data$results;
  const {
    isMobile
  } = useMediaQuery();
  const [pageNumber, setPageNumber] = usePagination(true);
  const pageCount = useRef(0);
  const {
    isLoggedIn
  } = useIsLoggedIn();
  const getResultCardClickHandler = (name, href, index, type) => () => {
    usageTracker.track('interaction', {
      action: 'catalog-results-item-click',
      name,
      href,
      index: index + 1,
      type
    });
  };
  const handlePageChange = ({
    target: {
      value
    }
  }) => {
    setPageNumber(value);
    if (window.scrollTo) window.scrollTo(0, 0);
    usageTracker.track('interaction', {
      action: 'catalog-results-change-page',
      value
    });
  };
  if (!loading && (error || !data)) return /*#__PURE__*/_jsxs(NavMarker, {
    name: "CATALOG_PAGE_LOAD_FAILURE",
    children: [/*#__PURE__*/_jsx(ACBadRequestError, {}), /*#__PURE__*/_jsx(DocumentHead, {
      children: /*#__PURE__*/_jsx("meta", {
        content: (error === null || error === void 0 || (_error$status = error.status) === null || _error$status === void 0 ? void 0 : _error$status.toString()) || '500',
        name: "prerender-status-code"
      })
    }), window.prerenderReady = true]
  });
  if (!loading && data) {
    pageCount.current = data.total && Math.ceil(data.total / PAGE_SIZE) || 0;
  }
  const displayPaginator = !!(pageCount.current > 1) && !disablePaginator;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [loading ? /*#__PURE__*/_jsx("div", {
      style: {
        height: 1700
      },
      children: /*#__PURE__*/_jsx("div", {
        "data-test-id": "catalog-loading",
        children: /*#__PURE__*/_jsx(UILoadingDots, {
          minHeight: 100
        })
      })
    }) : data !== null && data !== void 0 && (_data$results = data.results) !== null && _data$results !== void 0 && _data$results.length ? /*#__PURE__*/_jsxs(NavMarker, {
      name: "CATALOG_RESULTS_LOADED",
      children: [!disableSchema && /*#__PURE__*/_jsx(ResultsSchema, {
        resultsData: data
      }), /*#__PURE__*/_jsxs(_Fragment, {
        children: [window.prerenderReady = true, /*#__PURE__*/_jsx(CardGrid, {
          className: "m-bottom-15",
          children: data.results.map((result, index) => {
            const {
              href,
              footerHref
            } = getResultHrefs(result, isLoggedIn);
            const cardContentType = getCardContentType(result.contentType, result.hasAward);
            if (!cardContentType) return null;
            return /*#__PURE__*/_jsx(PublicContentCard, {
              badge: result.seoBadge,
              categories: result.categories
              // @ts-expect-error ...restProps is used here
              ,
              className: "m-all-0",
              description: result.description || '',
              duration: result.duration,
              footerHref: footerHref,
              href: href,
              name: result.name || '',
              numberOfLessons: result.numberOfLessons,
              onClick: getResultCardClickHandler(result.name || '', href, index, cardContentType),
              thumbnailUrl: result.previewThumbnailRectangleUrl,
              type: cardContentType
            }, result.id);
          })
        })]
      })]
    }) : /*#__PURE__*/_jsxs(NavMarker, {
      name: "CATALOG_PAGE_LOAD_EMPTY",
      children: [window.prerenderReady = true, /*#__PURE__*/_jsx(UIErrorMessage, {
        illustration: "empty-state-charts",
        style: {
          maxWidth: '380px',
          margin: '60px auto'
        },
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.catalogPage.noResultsTitle"
        }),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "academyPublic.catalogPage.noResultsDescription"
        })
      })]
    }), displayPaginator && /*#__PURE__*/_jsx(ThemedPaginator, {
      maxVisiblePageButtons: isMobile ? 0 : 6,
      onPageChange: handlePageChange,
      page: pageNumber,
      pageCount: pageCount.current,
      showFirstLastButtons: true
    })]
  });
};
const PublicCatalogResults = () => {
  const {
    publicCatalogData,
    publicCatalogError,
    publicCatalogLoading
  } = useGetCatalogResultsFromUrlFilters({
    pageSize: PAGE_SIZE
  });
  return /*#__PURE__*/_jsx(PublicCatalogResultsDisplay, {
    data: publicCatalogData,
    error: publicCatalogError,
    loading: publicCatalogLoading
  });
};
export default PublicCatalogResults;